import { intersection } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import useGetAuthSessionQuery from '~/features/auth/hooks/useGetAuthSessionQuery'
import useSignOut from '~/features/auth/hooks/useSignOut'
import { isLibbyDomain } from '~/features/partners/utils'
import { EClientPath, EMarketingClientPath } from '~/models/api'
import { ECognitoGroups } from '~/models/auth'
import { store } from '~/store'

type TProps = {
  requiredUserGroups: ECognitoGroups[] | null
  isMarketingPage?: boolean
  isPartnerAuthPage?: boolean
  isDemoPage?: boolean
}
const usePrivateRoutesRedirect = ({
  requiredUserGroups,
  isMarketingPage = false,
  isPartnerAuthPage = false,
  isDemoPage = false,
}: TProps) => {
  // todo: use hook directly in root layout, thus run it regardless of child layout. Create a map with roles and routes, and redirect condition & route to use for this purpose, and route declaration purpose.
  const { data: authSession, isLoading: isLoadingAuthSession } =
    useGetAuthSessionQuery()
  const [isAuthCheckComplete, setAuthCheckComplete] = useState(false)
  const navigate = useNavigate()
  const isPartner = !isLibbyDomain()
  const [setUser, isImpersonating] = store((state) => [
    state.setUser,
    !!state.user.impersonatorsUserId,
  ])
  const { signOut } = useSignOut()

  const checkPermission = useCallback(
    (groups?: string[]) => {
      if (requiredUserGroups && requiredUserGroups.length === 0) {
        return true
      }

      if (isImpersonating) {
        return false
      }

      return intersection(groups, requiredUserGroups).length > 0
    },
    [isImpersonating, requiredUserGroups],
  )

  const handleFetchAuthSession = useCallback(async () => {
    if (isMarketingPage) {
      // on marketing pages we need to redirect PARTNER USERS to /auth or to overview if they're logged in
      if (isPartner) {
        navigate(
          !authSession?.cognitoUid
            ? EClientPath.partnerAuth
            : EClientPath.overview,
        )
      }
      setAuthCheckComplete(true)
    } else if (isPartnerAuthPage) {
      // restrict access to /partner-auth for HEYLIBBY USERS
      if (!isPartner) {
        navigate(EMarketingClientPath.home)
      } else if (authSession?.groups.includes(ECognitoGroups.partnerAdmin)) {
        navigate(EClientPath.partnerAdmin)
      } else if (authSession?.groups.includes(ECognitoGroups.partnerEditor)) {
        navigate(EClientPath.partnerAdminUserManagement)
      } else if (authSession?.cognitoUid) {
        navigate(EClientPath.overview)
      }

      setAuthCheckComplete(true)
    } else {
      // if it's not a marketing page, check other permissions
      if (!authSession?.cognitoUid) {
        if (isDemoPage) {
          setAuthCheckComplete(true)
          return
        }

        if (
          ![EMarketingClientPath.home, EClientPath.partnerAuth].includes(
            window.location.pathname as EClientPath,
          )
        ) {
          setUser({
            signInRedirectPath: window.location.pathname as EClientPath,
          })
        }

        signOut()
        return
        // check extra permissions for logged in users and redirect to home or to /overview for PARTNER USERS
      }

      if (authSession?.cognitoUid) {
        // if requiredUserGroups is null, it means that we don't need to check permissions
        if (!requiredUserGroups) {
          setAuthCheckComplete(true)
          return
        }

        if (!checkPermission(authSession.groups)) {
          const { pathname } = window.location

          navigate(EClientPath.overview, {
            state: { previousPathname: pathname },
          })
        }
      }

      setAuthCheckComplete(true)
    }
  }, [
    authSession?.cognitoUid,
    authSession?.groups,
    checkPermission,
    isDemoPage,
    isMarketingPage,
    isPartner,
    isPartnerAuthPage,
    navigate,
    requiredUserGroups,
    setUser,
    signOut,
  ])

  useEffect(() => {
    if (isLoadingAuthSession) {
      return
    }

    handleFetchAuthSession()
  }, [handleFetchAuthSession, isLoadingAuthSession])

  return isAuthCheckComplete
}
export default usePrivateRoutesRedirect
